import type { ApproveWorkPeriodNode } from './types.graphql'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import { Checkmark } from '@/components_deprecated/Table/cells'
import { useOnErrorAuto, useUser } from '@/utils/hooks'
import { ApproveCommentModal } from './ApproveCommentModal'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useMutation } from '@apollo/client'
import { PATCH_WORK_PERIOD } from './mutations'
import styled, { useTheme } from 'styled-components'
import { WorkPeriodDetail } from './WorkPeriodDetail'
import { combineDatesToReadableString } from '@/utils/date/combineDatesToReadableString'
import { getWeek } from '@/utils/date-fns/getWeek'
import { DietButton } from './WorkPeriodGroup/DietButton'
import { Card } from '@/components_deprecated/Card'
import { useWorkPeriodApprovePermissions } from './hooks'
import { AddonTextAndAction } from '../activities/components/overview/AddonTextAndAction'
import { H4 } from '@/components'

const CommentText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.3;
`
const GroupWrapper = styled.div`
  margin: 2rem 0;
`

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;

  > h3 {
    margin: 1rem 0 0.25rem 0;
  }
  > h4 {
    margin: 1rem 0 0.5rem 0;
  }
`

const DietWrapper = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
`

interface ApprovalWrapperProps {
  $approved: boolean | null
}

const ApprovalWrapper = styled.div<ApprovalWrapperProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  padding: 1rem;

  border-radius: 8px;
  background-color: ${props =>
    props.$approved
      ? props.theme.colors.green100
      : props.$approved === null
      ? 'transparent'
      : props.theme.colors.red100};
  border: 1px solid ${props => props.theme.colors.gray8};

  &:first-child {
    border-right: 1px solid ${props => props.theme.colors.gray8};
  }
`

const ApprovalHalf = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  max-width: 100%;
`

const PlannedWork = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 16ch;
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: pre;
`

const PlannedWorkAndIcon = styled.span`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  margin-top: 2rem;
`

const AddonsAsTextWrapper = styled.div`
  margin-top: 1rem;
  border-top: 1px solid ${props => props.theme.colors.gray8};
  padding-top: 1rem;
`

export const WorkPeriodApproveCards: React.FC<{
  workPeriods: ApproveWorkPeriodNode[]
  loading: boolean
  initRefetch: () => void
}> = ({ workPeriods, initRefetch }) => {
  const translations = useTranslate({
    user: 'common.user',
    date: 'common.date',
    addons: 'salary.addons',
    approvedByUser: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',
    comment: 'common.comment',
    managerComment: 'common.manager-comment',
    totalWork: 'common.total-work',
    weekN: ['common.week-n', { n: '0' }],
    diet: 'common.diet',
  })

  const me = useUser()
  const theme = useTheme()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()

  const { workPeriodApprovalState } = useWorkPeriodApprovePermissions()

  const [patchWorkPeriod] = useMutation(PATCH_WORK_PERIOD, {
    onError: onErrorAuto(),
    update(cache, { data }) {
      const workPeriod = data?.patchWorkPeriod.workPeriod
      if (!workPeriod) return

      cache.modify({
        id: cache.identify(workPeriod),
        fields: {
          calendarItem: () => workPeriod.calendarItem,
          approvedByUser: () => workPeriod.approvedByUser,
          approvedByManager: () => workPeriod.approvedByManager,
        },
      })
    },
  })

  async function handleApproveWorkPeriodDriver(
    workPeriod: ApproveWorkPeriodNode
  ) {
    const { data } = await addPrompt<{
      comment: string
      approved: boolean
    } | null>(resolve => (
      <ApproveCommentModal
        existingComment={workPeriod.userComment}
        existingApproved={workPeriod.approvedByUser}
        onSubmit={resolve}
      />
    ))
    if (!data) return
    void patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          approvedByUser: data.approved,
          userComment: data.comment,
        },
      },
    })
    initRefetch()
  }

  async function handleApproveWorkPeriodManager(
    workPeriod: ApproveWorkPeriodNode
  ) {
    const { data } = await addPrompt<{
      comment: string
      approved: boolean
    } | null>(resolve => (
      <ApproveCommentModal
        existingComment={workPeriod.managerComment}
        existingApproved={workPeriod.approvedByManager}
        onSubmit={resolve}
      />
    ))
    if (!data) return

    void patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          approvedByManager: data.approved,
          managerComment: data.comment,
        },
      },
    })
    initRefetch()
  }

  async function showWorkPeriodDetails(workPeriod: ApproveWorkPeriodNode) {
    await addPrompt<{ id: string } | null>(resolve => (
      <WorkPeriodDetail workPeriodId={workPeriod.id} onSubmit={resolve} />
    ))
  }

  // Group work periods by week
  const workPeriodGroups = workPeriods.reduce(
    (acc, workPeriod) => {
      const weekStart = getWeek(parseISO(workPeriod.datetimeStart))

      if (!acc[weekStart]) {
        acc[weekStart] = []
      }
      acc[weekStart].push(workPeriod)

      return acc
    },
    {} as Record<string, ApproveWorkPeriodNode[]>
  )

  return (
    <span>
      {Object.entries(workPeriodGroups).map(group => {
        const weekNumber = group[0]
        const workPeriodGroup = group[1]

        return (
          <GroupWrapper key={weekNumber + workPeriodGroup[0]?.id}>
            <h2>{translations.weekN({ n: weekNumber })}</h2>
            {workPeriodGroup.map(workPeriod => {
              const {
                driverApproveDirty,
                managerApproveDirty,
                canApproveAsDriver,
                canApproveAsManager,
              } = workPeriodApprovalState(me.id, workPeriod)

              return (
                <StyledCard key={workPeriod.id}>
                  <h3>{workPeriod.user.fullName}</h3>
                  <PlannedWorkAndIcon>
                    <PlannedWork>
                      <b>
                        {workPeriod.calendarItem &&
                          combineDatesToReadableString(
                            parseISO(workPeriod.calendarItem.start),
                            parseISO(workPeriod.calendarItem.end),
                            false
                          )}
                      </b>
                      <span>
                        {combineDatesToReadableString(
                          parseISO(workPeriod.datetimeStart),
                          parseISO(workPeriod.datetimeEnd),
                          false
                        )}
                      </span>
                    </PlannedWork>

                    <Icon
                      icon="circle-info"
                      type="solid"
                      size="28"
                      cursor="pointer"
                      margin="0 0 0 0.5rem"
                      color={theme.colors.secondary}
                      onClick={() => showWorkPeriodDetails(workPeriod)}
                    />
                  </PlannedWorkAndIcon>

                  <DietWrapper>
                    <DietButton
                      workPeriod={workPeriod}
                      userId={workPeriod.user.id}
                      canEdit={
                        canApproveAsManager ||
                        (!workPeriod.approvedByManager &&
                          !workPeriod.approvedByUser)
                      }
                      border={true}
                      onChange={() => initRefetch()}
                      restrictWidth
                    />
                    <AddonTextAndAction
                      userId={workPeriod.user.id}
                      datetimeStart={parseISO(workPeriod.datetimeStart)}
                      datetimeEnd={parseISO(workPeriod.datetimeEnd)}
                      addonsAsText={workPeriod.addonsAsText}
                      restrictWidth
                      hideText={true}
                      border
                    />
                  </DietWrapper>

                  {/* User comment / approval */}
                  <h4>{translations.approvedByUser}</h4>
                  <ApprovalWrapper
                    $approved={
                      workPeriod.approvedByUser
                        ? true
                        : !driverApproveDirty
                        ? null
                        : false
                    }
                  >
                    <ApprovalHalf
                      onClick={() =>
                        canApproveAsDriver &&
                        handleApproveWorkPeriodDriver(workPeriod)
                      }
                    >
                      <Checkmark
                        checked={workPeriod.approvedByUser}
                        icon={!driverApproveDirty ? 'circle-plus' : undefined}
                        color={
                          !driverApproveDirty ? theme.colors.gray3 : undefined
                        }
                      />
                      {workPeriod.userComment && (
                        <CommentText>{workPeriod.userComment}</CommentText>
                      )}
                    </ApprovalHalf>
                  </ApprovalWrapper>

                  <h4>{translations.approvedByManager}</h4>
                  <ApprovalWrapper
                    $approved={
                      workPeriod.approvedByManager
                        ? true
                        : !managerApproveDirty
                        ? null
                        : false
                    }
                  >
                    <ApprovalHalf
                      onClick={() => {
                        if (!canApproveAsManager) return
                        void handleApproveWorkPeriodManager(workPeriod)
                      }}
                    >
                      <Checkmark
                        checked={workPeriod.approvedByManager}
                        icon={!managerApproveDirty ? 'circle-plus' : undefined}
                        color={
                          !managerApproveDirty ? theme.colors.gray3 : undefined
                        }
                      />
                      {workPeriod.managerComment && (
                        <CommentText>{workPeriod.managerComment}</CommentText>
                      )}
                    </ApprovalHalf>
                  </ApprovalWrapper>
                  <AddonsAsTextWrapper>
                    {workPeriod.addonsAsText.length > 0 && (
                      <H4>{translations.addons}</H4>
                    )}
                    {workPeriod.addonsAsText.map((text, index) => (
                      <CommentText key={index}>{text}</CommentText>
                    ))}
                  </AddonsAsTextWrapper>
                </StyledCard>
              )
            })}
          </GroupWrapper>
        )
      })}
    </span>
  )
}
